import { PropsWithChildren } from 'react';

import { useGraphQLQuery } from '@betterment/js-runtime';
import { Box, Button, Card, Heading, Link, Shimmer, Text } from '@betterment/samba-react';

import { CommonReportsCreationOptionsDocument } from './graphql/query.reportCreationOptions.generated';

import { ErrorBoundary } from '../../../components/error-boundary/ErrorBoundary';
import { navigate } from '../../../utils/pathHelpers';
import { urlBuilder } from '../../../utils/urlBuilder';

const reportBoxCss = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  borderStyle: 'solid',
  borderWidth: 'thin',
  borderColor: 'border',
  borderRadius: 'medium',
  cursor: 'pointer',
  padding: 'space.200',
  textDecoration: 'none',
  color: 'inherit',
};

const shimmerCss = {
  alignItems: 'center',
  height: 'size.200',
  width: '100%',
};

interface CommonReportsProps {
  employerId: string;
}

const ShimmerLoader = () => {
  return (
    <>
      {[...Array(3).keys()].map(key => (
        <Box key={key} css={reportBoxCss}>
          <Shimmer css={shimmerCss} />
          <Shimmer
            css={{
              ...shimmerCss,
              marginTop: 'space.100',
            }}
          />
        </Box>
      ))}
    </>
  );
};

const CommonReportsContent = ({ employerId }: { employerId: string }) => {
  const { data } = useGraphQLQuery(CommonReportsCreationOptionsDocument, {
    variables: { employerId },
    throwOnError: true,
  });
  const commonReportOptions = data?.employer.reportCreationOptions.filter(report => report.isCommon);

  return (
    <Box
      css={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 'space.150',
      }}
    >
      {!data && <ShimmerLoader />}
      {commonReportOptions?.map(report => (
        <Box asChild={true} key={report.id} css={reportBoxCss}>
          <Link href={urlBuilder.reportsNew({ report_type: report.name })}>
            <Heading as="h3" variant="title100">
              {report.name}
            </Heading>
            <Text variant="text50" color="text.weak">
              {report.description}
            </Text>
          </Link>
        </Box>
      ))}
    </Box>
  );
};

const CommonReports: React.FC<PropsWithChildren<CommonReportsProps>> = ({ employerId }) => {
  return (
    <Card css={{ p: 'space.300', display: 'flex', flexDirection: 'column' }}>
      <ErrorBoundary name="commonReports">
        <Card.Header
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Heading as="h2" css={{ mb: 'space.300' }} variant="title200">
            Common reports
          </Heading>
          <Button onClick={() => navigate(urlBuilder.reportsNew())} css={{ mb: 'space.150' }}>
            Run report
          </Button>
        </Card.Header>
        <CommonReportsContent employerId={employerId} />
      </ErrorBoundary>
    </Card>
  );
};

export default CommonReports;
